import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DataView } from "primereact/dataview";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { ContextMenu } from "primereact/contextmenu";
import { ContextMenu as ContextMenuType } from "primereact/contextmenu";
import { MenuItem } from "primereact/menuitem";
import { confirmPopup } from "primereact/confirmpopup";
import { toast } from "react-toastify";
import LinkedKnowledgeArticlesInfoButton from "../../Components/Custom/KnowledgeBase/LinkedKnowledgeArticlesInfoButton";
import { PageFilterDropdown, PageFilterSearch, PageFilters } from "../../Components/Crud/PageFilters";
import { SmartFilterButton } from "../../Components/inputs/SmartFilterButton/SmartFilterButton";
import { usePageHeader } from "../../Components/Template/PageTitle/PageContext";
import MultiCustomerCheck from "../../Components/MultiCustomerCheck";
import PermissionCheck from "../../Components/PermissionCheck";
import { RESOURCE_LOCATION } from "../../Constants/resourceLocations";
import { IKnowledgeArticleModel, KnowledgeArticleService } from "../../Services/KnowledgeArticleService";
import { IKnowledgeCategoryModel, KnowledgeCategoryService } from "../../Services/KnowledgeCategoryService";
import {
    KNOWLEDGE_ARTICLE_CREATE_PATH,
    KNOWLEDGE_ARTICLE_PAGE_SIZE,
    KNOWLEDGE_ARTICLE_ROWS_PER_PAGE_OPTIONS,
    KNOWLEDGE_ARTICLE_SPOTLIGHT_PAGE_SIZE,
    KNOWLEDGE_ARTICLE_STATUS,
    KNOWLEDGE_BASE_ALL_CATEGORIES_VALUE,
    KNOWLEDGE_BASE_NARROW_MAX_WIDTH,
    formatKnowledgeArticleTimestamp,
    isFeaturedActive,
    isFeaturedExpired,
    knowledgeArticleDetailPath,
    knowledgeArticleEditPath,
    resolveKnowledgeArticleIconClass,
    resolveKnowledgeCategoryIconClass,
    unwrapCrudList,
    unwrapCrudListTotal,
} from "./knowledgeBaseConstants";
import { KnowledgeBaseGcpCreateDisabledBanner } from "./KnowledgeBaseGcpProjectGate";
import { useKnowledgeBaseCreateAccess } from "./useKnowledgeBaseCreateAccess";
import { resolveApiErrorMessage } from "../../Util/apiErrorUtil";
import ManageKnowledgeCategoriesDialog from "./ManageKnowledgeCategoriesDialog";
import KnowledgeBaseAskAi from "./KnowledgeBaseAskAi";
import "./KnowledgeBase.scss";

const SEARCH_DEBOUNCE_MS = 300;

const KnowledgeBase: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setPageHeader, resetPageHeader } = usePageHeader();

    const knowledgeArticleService = useMemo(() => new KnowledgeArticleService(), []);
    const knowledgeCategoryService = useMemo(() => new KnowledgeCategoryService(), []);
    const canManage = useMemo(() => PermissionCheck("KnowledgeBase.Manage"), []);
    // Defined up here because the context-menu useMemo below reads it while rendering.
    // An article shared down from a customer above is read-only, also for a KnowledgeBase.Manage holder.
    const canManageArticle = (article?: IKnowledgeArticleModel | null) => canManage && article?.manageable !== false;
    // A multi-customer user picks the owning customer in the editor, which checks that customer there; for
    // everybody else "create" depends on their own customer. Readers cannot create at all: nothing to ask.
    const isMultiCustomer = useMemo(() => MultiCustomerCheck() as boolean, []);
    const createAccessStatus = useKnowledgeBaseCreateAccess(undefined, canManage && !isMultiCustomer);
    const canCreateArticles = canManage && createAccessStatus === "allowed";

    const [categories, setCategories] = useState<IKnowledgeCategoryModel[]>([]);
    const [selectedCategoryUuid, setSelectedCategoryUuid] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    // Readers of a sub-customer also get what the customer(s) above them shared down. This filter leaves
    // those out; it only ever removes rows, so it is offered to whoever actually has such articles.
    const [ownOnly, setOwnOnly] = useState(false);
    const [hasSharedArticles, setHasSharedArticles] = useState(false);
    // What the list is actually filtered on: the search box's value once the user pauses typing.
    const [appliedSearch, setAppliedSearch] = useState("");
    const [listPage, setListPage] = useState(0);
    const [pageSize, setPageSize] = useState(KNOWLEDGE_ARTICLE_PAGE_SIZE);
    const [totalElements, setTotalElements] = useState(0);

    const [allArticles, setAllArticles] = useState<IKnowledgeArticleModel[]>([]);
    const [spotlightArticles, setSpotlightArticles] = useState<IKnowledgeArticleModel[]>([]);
    const [spotlightPage, setSpotlightPage] = useState(0);
    const [spotlightTotal, setSpotlightTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    // Typing in the search box fires a request per pause; only the answer to the latest one may be shown.
    const articleRequestRef = useRef(0);
    const spotlightRequestRef = useRef(0);

    const [contextMenuTargetEl, setContextMenuTargetEl] = useState<HTMLElement | null>(null);
    const [selectedContextCategory, setSelectedContextCategory] = useState<IKnowledgeCategoryModel | null>(null);
    const [deletingCategoryUuid, setDeletingCategoryUuid] = useState<string | null>(null);
    const categoryContextMenuRef = useRef<ContextMenuType>(null);
    const [selectedContextArticle, setSelectedContextArticle] = useState<IKnowledgeArticleModel | null>(null);
    const [deletingArticleUuid, setDeletingArticleUuid] = useState<string | null>(null);
    const articleContextMenuRef = useRef<ContextMenuType>(null);
    const [manageCategoriesVisible, setManageCategoriesVisible] = useState(false);
    const [spotlightOpen, setSpotlightOpen] = useState(
        () => !window.matchMedia(`(max-width: ${KNOWLEDGE_BASE_NARROW_MAX_WIDTH}px)`).matches
    );

    useEffect(() => {
        setPageHeader({
            title: { value: "knowledgeBase.title", translationKey: true },
            subtitle: { value: "knowledgeBase.subtitle", translationKey: true },
            actions: (
                <>
                    <LinkedKnowledgeArticlesInfoButton location={RESOURCE_LOCATION.KNOWLEDGE_ARTICLE} />
                    {canManage ? (
                        <>
                            {/* A disabled button fires no mouse events, so the hint hangs on a wrapper of our
                                own. Not PrimeReact's `showOnDisabled`: that one moves the button into a DIV it
                                creates itself, behind React's back, and React then cannot take the page header
                                apart again ("removeChild: not a child of this node"). */}
                            {!canCreateArticles && (
                                <Tooltip target=".knowledge-base-create-action" position="bottom" />
                            )}
                            <span
                                className="knowledge-base-create-action"
                                data-pr-tooltip={
                                    canCreateArticles
                                        ? undefined
                                        : (t("knowledgeBase.gcpRequired.createDisabled.title") as string)
                                }
                            >
                                <Button
                                    label={t("knowledgeBase.newArticle") as string}
                                    icon="pi pi-plus"
                                    disabled={!canCreateArticles}
                                    onClick={() => navigate(KNOWLEDGE_ARTICLE_CREATE_PATH)}
                                />
                            </span>
                        </>
                    ) : null}
                </>
            ),
        });
        return () => resetPageHeader();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canManage, canCreateArticles, t]);

    // Every category comes with its article count: one request for the whole sidebar.
    const loadCategories = useCallback(async () => {
        try {
            const response = await knowledgeCategoryService.fetchItems({ includeInactive: false });
            setCategories(unwrapCrudList<IKnowledgeCategoryModel>(response.data.data));
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.loadError", t));
        }
    }, [knowledgeCategoryService, t]);

    const loadArticles = useCallback(async () => {
        const request = ++articleRequestRef.current;
        setLoading(true);
        try {
            // Admins with manage rights see every status (draft/published/archived) so a just-created
            // article never silently disappears; regular viewers only ever see published+in-window ones.
            const response = await knowledgeArticleService.fetchItems({
                visibleOnly: canManage ? undefined : true,
                search: appliedSearch || undefined,
                categoryUuid: selectedCategoryUuid || undefined,
                ownOnly: ownOnly || undefined,
                page: listPage,
                size: pageSize,
            });
            if (request !== articleRequestRef.current) {
                return;
            }
            const pageData = response.data.data;
            const nextTotal = unwrapCrudListTotal<IKnowledgeArticleModel>(pageData);
            const articles = unwrapCrudList<IKnowledgeArticleModel>(pageData);
            // Sticky: once this reader has seen an article from above, the filter stays on offer — also
            // while it is switched on, and while a search happens to match none of them.
            if (articles.some((article) => article.manageable === false)) {
                setHasSharedArticles(true);
            }
            setAllArticles(articles);
            setTotalElements(nextTotal);
            const maxPage = Math.max(0, Math.ceil(nextTotal / pageSize) - 1);
            if (listPage > maxPage) {
                setListPage(maxPage);
            }
        } catch (error) {
            if (request === articleRequestRef.current) {
                toast.error(resolveApiErrorMessage(error, "knowledgeBase.loadError", t));
            }
        } finally {
            if (request === articleRequestRef.current) {
                setLoading(false);
            }
        }
    }, [knowledgeArticleService, selectedCategoryUuid, appliedSearch, canManage, ownOnly, listPage, pageSize, t]);

    // Featured + the user's own pins as one server-paged set (each article once), so the card grid
    // stays bounded however many articles get featured or pinned.
    const loadSpotlight = useCallback(async () => {
        const request = ++spotlightRequestRef.current;
        try {
            const response = await knowledgeArticleService.fetchItems({
                visibleOnly: true,
                spotlightOnly: true,
                ownOnly: ownOnly || undefined,
                page: spotlightPage,
                size: KNOWLEDGE_ARTICLE_SPOTLIGHT_PAGE_SIZE,
            });
            if (request !== spotlightRequestRef.current) {
                return;
            }
            const pageData = response.data.data;
            const nextTotal = unwrapCrudListTotal<IKnowledgeArticleModel>(pageData);
            setSpotlightArticles(unwrapCrudList<IKnowledgeArticleModel>(pageData));
            setSpotlightTotal(nextTotal);
            const maxPage = Math.max(0, Math.ceil(nextTotal / KNOWLEDGE_ARTICLE_SPOTLIGHT_PAGE_SIZE) - 1);
            if (spotlightPage > maxPage) {
                setSpotlightPage(maxPage);
            }
        } catch {
            // The spotlight grid is an extra on top of the list below it, which reports its own failure.
        }
    }, [knowledgeArticleService, ownOnly, spotlightPage]);

    useEffect(() => {
        loadCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setAppliedSearch(search.trim());
            setListPage(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        loadArticles();
    }, [loadArticles]);

    useEffect(() => {
        loadSpotlight();
    }, [loadSpotlight]);

    const selectCategory = (uuid: string | null) => {
        setSelectedCategoryUuid(uuid);
        setListPage(0);
    };

    const categoryFilterOptions = useMemo(
        () => [
            {
                uuid: KNOWLEDGE_BASE_ALL_CATEGORIES_VALUE,
                name: t("knowledgeBase.allCategories") as string,
                icon: "pi pi-th-large",
            },
            ...categories,
        ],
        [categories, t]
    );

    const renderCategoryFilterOption = (option: IKnowledgeCategoryModel) => {
        if (!option) {
            return null;
        }
        return (
            <span className="knowledge-base-page__category-option">
                <i className={resolveKnowledgeCategoryIconClass(option.icon)} />
                <span className="knowledge-base-page__category-option-name">{option.name}</span>
                {option.articleCount != null && (
                    <span className="knowledge-base-page__count">{option.articleCount}</span>
                )}
            </span>
        );
    };

    const handleCategoriesChanged = (deletedUuids: string[]) => {
        if (selectedCategoryUuid && deletedUuids.includes(selectedCategoryUuid)) {
            selectCategory(null);
        }
        loadCategories();
    };

    const deleteCategory = (category: IKnowledgeCategoryModel) => {
        const label = category.name || (t("knowledgeBase.category.deleteFallbackLabel") as string);
        const target = contextMenuTargetEl;
        const runDelete = async () => {
            setDeletingCategoryUuid(category.uuid);
            try {
                await knowledgeCategoryService.deleteItem(category.uuid);
                toast.success(t("knowledgeBase.category.deleteSuccess") as string);
                if (selectedCategoryUuid === category.uuid) {
                    selectCategory(null);
                }
                loadCategories();
            } catch {
                toast.error(t("knowledgeBase.category.deleteError") as string);
            } finally {
                setDeletingCategoryUuid(null);
            }
        };
        if (!target) {
            runDelete();
            return;
        }
        confirmPopup({
            target,
            message: t("processflow.list.deleteConfirm", { label }) as string,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("processflow.list.deleteAccept") as string,
            rejectLabel: t("processflow.list.deleteReject") as string,
            acceptClassName: "p-button-danger",
            accept: runDelete,
        });
    };

    const categoryContextMenuItems = useMemo<MenuItem[]>(() => ([
        {
            label: deletingCategoryUuid && selectedContextCategory?.uuid === deletingCategoryUuid
                ? t("processflow.list.context.deleting")
                : t("common.action.delete"),
            icon: "pi pi-trash",
            command: () => {
                if (!selectedContextCategory) {
                    return;
                }
                deleteCategory(selectedContextCategory);
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ]), [deletingCategoryUuid, selectedContextCategory, t]);

    const deleteArticle = (article: IKnowledgeArticleModel) => {
        const label = article.title || (t("knowledgeBase.deleteFallbackLabel") as string);
        const target = contextMenuTargetEl;
        const runDelete = async () => {
            setDeletingArticleUuid(article.uuid);
            try {
                await knowledgeArticleService.deleteItem(article.uuid);
                toast.success(t("knowledgeBase.deleteSuccess") as string);
                loadArticles();
                loadSpotlight();
                loadCategories();
            } catch {
                toast.error(t("knowledgeBase.deleteError") as string);
            } finally {
                setDeletingArticleUuid(null);
            }
        };
        if (!target) {
            runDelete();
            return;
        }
        confirmPopup({
            target,
            message: t("processflow.list.deleteConfirm", { label }) as string,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("processflow.list.deleteAccept") as string,
            rejectLabel: t("processflow.list.deleteReject") as string,
            acceptClassName: "p-button-danger",
            accept: runDelete,
        });
    };

    const articleContextMenuItems = useMemo<MenuItem[]>(() => ([
        {
            label: t("common.action.view"),
            icon: "pi pi-eye",
            command: () => {
                if (!selectedContextArticle) {
                    return;
                }
                navigate(knowledgeArticleDetailPath(selectedContextArticle.uuid));
            },
        },
        ...(canManageArticle(selectedContextArticle)
            ? [{
                label: t("common.action.edit"),
                icon: "pi pi-pencil",
                command: () => {
                    if (!selectedContextArticle) {
                        return;
                    }
                    navigate(knowledgeArticleEditPath(selectedContextArticle.uuid));
                },
            }]
            : []),
        ...(canManageArticle(selectedContextArticle)
            ? [
                { separator: true },
                {
                    label: deletingArticleUuid && selectedContextArticle?.uuid === deletingArticleUuid
                        ? t("processflow.list.context.deleting")
                        : t("common.action.delete"),
                    icon: "pi pi-trash",
                    command: () => {
                        if (!selectedContextArticle) {
                            return;
                        }
                        deleteArticle(selectedContextArticle);
                    },
                },
            ]
            : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ]), [canManage, deletingArticleUuid, navigate, selectedContextArticle, t]);

    const togglePin = async (article: IKnowledgeArticleModel, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            const response = await knowledgeArticleService.togglePin(article.uuid);
            const pinned = response.data.data === true;
            setAllArticles((current) => current.map((item) => (item.uuid === article.uuid ? { ...item, pinned } : item)));
            loadSpotlight();
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
        }
    };

    const isPinned = (article: IKnowledgeArticleModel) => article.pinned === true;

    const renderGlobalBadge = (article: IKnowledgeArticleModel, className: string) =>
        article.global ? (
            <span className={`${className} ${className}--global`}>
                <i className="pi pi-share-alt" />
                {t(article.manageable === false ? "knowledgeBase.badge.sharedFromAbove" : "knowledgeBase.badge.global")}
            </span>
        ) : null;

    const renderCard = (article: IKnowledgeArticleModel) => {
        const categoryName = article.categoryNames?.[0];
        return (
        <div
            className="knowledge-article-card"
            key={article.uuid}
            onClick={() => navigate(knowledgeArticleDetailPath(article.uuid))}
            onContextMenu={(event) => {
                event.preventDefault();
                setSelectedContextArticle(article);
                setContextMenuTargetEl(event.currentTarget as HTMLElement);
                articleContextMenuRef.current?.show(event);
            }}
        >
            <div className="knowledge-article-card__top">
                <div className="knowledge-article-card__top-left">
                    <div className="knowledge-article-card__icon">
                        <i className={resolveKnowledgeArticleIconClass(article.icon)} />
                    </div>
                    {categoryName && <span className="knowledge-article-card__badge">{categoryName}</span>}
                </div>
                <div className="knowledge-article-card__actions">
                    <button
                        type="button"
                        className="knowledge-article-card__action-btn"
                        onClick={(e) => togglePin(article, e)}
                        aria-label={t("knowledgeBase.togglePin") as string}
                    >
                        <i className={isPinned(article) ? "pi pi-bookmark-fill" : "pi pi-bookmark"} />
                    </button>
                </div>
            </div>

            <h4 className="knowledge-article-card__title">{article.title}</h4>

            <div className="knowledge-article-card__footer">
                <div className="knowledge-article-card__badges">
                    {isFeaturedActive(article) && (
                        <span className="knowledge-article-card__badge knowledge-article-card__badge--accent">
                            <i className="pi pi-star-fill" />
                            {t("knowledgeBase.featured")}
                        </span>
                    )}
                    {isPinned(article) && (
                        <span className="knowledge-article-card__badge knowledge-article-card__badge--pinned">
                            <i className="pi pi-bookmark-fill" />
                            {t("knowledgeBase.pinned")}
                        </span>
                    )}
                    {renderGlobalBadge(article, "knowledge-article-card__badge")}
                    {canManageArticle(article) && isFeaturedExpired(article) && (
                        <span className="knowledge-article-card__badge knowledge-article-card__badge--status-archived">
                            <i className="pi pi-star" />
                            {t("knowledgeBase.featuredExpired")}
                        </span>
                    )}
                    {canManageArticle(article) && article.status !== KNOWLEDGE_ARTICLE_STATUS.PUBLISHED && (
                        <span
                            className={`knowledge-article-card__badge knowledge-article-card__badge--status-${article.status.toLowerCase()}`}
                        >
                            {t(`knowledgeBase.status.${article.status.toLowerCase()}`)}
                        </span>
                    )}
                </div>
            </div>
        </div>
        );
    };

    const renderRow = (article: IKnowledgeArticleModel) => {
        const categoryName = article.categoryNames?.[0];
        return (
            <div
                className="knowledge-article-row"
                key={article.uuid}
                onClick={() => navigate(knowledgeArticleDetailPath(article.uuid))}
                onContextMenu={(event) => {
                    event.preventDefault();
                    setSelectedContextArticle(article);
                    setContextMenuTargetEl(event.currentTarget as HTMLElement);
                    articleContextMenuRef.current?.show(event);
                }}
            >
                <div className="knowledge-article-row__icon">
                    <i className={resolveKnowledgeArticleIconClass(article.icon)} />
                </div>
                <div className="knowledge-article-row__main">
                    <div className="knowledge-article-row__title-line">
                        <span className="knowledge-article-row__title">{article.title}</span>
                        {categoryName && <span className="knowledge-article-row__badge">{categoryName}</span>}
                        {isFeaturedActive(article) && (
                            <span className="knowledge-article-row__badge knowledge-article-row__badge--accent">
                                <i className="pi pi-star-fill" />
                                {t("knowledgeBase.featured")}
                            </span>
                        )}
                        {isPinned(article) && (
                            <span className="knowledge-article-row__badge knowledge-article-row__badge--pinned">
                                <i className="pi pi-bookmark-fill" />
                                {t("knowledgeBase.pinned")}
                            </span>
                        )}
                        {renderGlobalBadge(article, "knowledge-article-row__badge")}
                        {canManageArticle(article) && article.status !== KNOWLEDGE_ARTICLE_STATUS.PUBLISHED && (
                            <span
                                className={`knowledge-article-row__badge knowledge-article-row__badge--status-${article.status.toLowerCase()}`}
                            >
                                {t(`knowledgeBase.status.${article.status.toLowerCase()}`)}
                            </span>
                        )}
                    </div>
                </div>
                {article.updatedAt && (
                    <span
                        className="knowledge-article-row__updated"
                        title={
                            (article.updatedByName
                                ? t("knowledgeBase.lastUpdatedBy", { name: article.updatedByName })
                                : t("knowledgeBase.lastUpdated")) as string
                        }
                    >
                        <i className="pi pi-clock" />
                        {formatKnowledgeArticleTimestamp(article.updatedAt)}
                    </span>
                )}
                {/* Editing and deleting are in the context menu; only the personal bookmark sits on the row. */}
                <div className="knowledge-article-row__actions">
                    <button
                        type="button"
                        className="knowledge-article-row__action-btn"
                        onClick={(e) => togglePin(article, e)}
                        aria-label={t("knowledgeBase.togglePin") as string}
                    >
                        <i className={isPinned(article) ? "pi pi-bookmark-fill" : "pi pi-bookmark"} />
                    </button>
                    <i className="pi pi-chevron-right knowledge-article-row__chevron" />
                </div>
            </div>
        );
    };

    // PrimeReact 10 DataView uses listTemplate (full array), not the removed itemTemplate API.
    const articleListTemplate = (items?: IKnowledgeArticleModel[]) => (
        <div className="knowledge-base-page__grid">
            {(items ?? []).map((article) => renderCard(article))}
        </div>
    );

    const articleRowsTemplate = (items?: IKnowledgeArticleModel[]) => (
        <div className="knowledge-base-page__list">
            {(items ?? []).map((article) => renderRow(article))}
        </div>
    );

    return (
        <div className="knowledge-base-page">
            <div className="knowledge-base-page__layout">
                <aside className="knowledge-base-page__sidebar">
                    <div className="knowledge-base-page__sidebar-header">
                        <span className="knowledge-base-page__sidebar-label">{t("knowledgeBase.categoriesLabel")}</span>
                        {canManage && (
                            <Button
                                type="button"
                                text
                                size="small"
                                icon="pi pi-cog"
                                label={t("knowledgeBase.category.manage") as string}
                                className="knowledge-base-page__sidebar-manage"
                                onClick={() => setManageCategoriesVisible(true)}
                            />
                        )}
                    </div>
                    <div
                        className={`knowledge-base-page__category ${!selectedCategoryUuid ? "knowledge-base-page__category--active" : ""}`}
                        onClick={() => selectCategory(null)}
                    >
                        <i className="pi pi-th-large" />
                        <span className="knowledge-base-page__category-name">{t("knowledgeBase.allCategories")}</span>
                    </div>
                    {categories.map((category) => (
                        <div
                            key={category.uuid}
                            className={`knowledge-base-page__category ${selectedCategoryUuid === category.uuid ? "knowledge-base-page__category--active" : ""}`}
                            onClick={() => selectCategory(category.uuid)}
                            onContextMenu={(event) => {
                                if (!canManage || category.manageable === false) {
                                    return;
                                }
                                event.preventDefault();
                                setSelectedContextCategory(category);
                                setContextMenuTargetEl(event.currentTarget as HTMLElement);
                                categoryContextMenuRef.current?.show(event);
                            }}
                        >
                            <i className={resolveKnowledgeCategoryIconClass(category.icon)} />
                            <span className="knowledge-base-page__category-name">{category.name}</span>
                            <span className="knowledge-base-page__count">{category.articleCount ?? 0}</span>
                        </div>
                    ))}
                </aside>

                {canManage && <ContextMenu model={categoryContextMenuItems} ref={categoryContextMenuRef} />}
                <ContextMenu model={articleContextMenuItems} ref={articleContextMenuRef} />
                {canManage && (
                    <ManageKnowledgeCategoriesDialog
                        visible={manageCategoriesVisible}
                        categories={categories.filter((category) => category.manageable !== false)}
                        onHide={() => setManageCategoriesVisible(false)}
                        onChanged={handleCategoriesChanged}
                    />
                )}

                <div className="knowledge-base-page__content">
                    {canManage && createAccessStatus === "blocked" && <KnowledgeBaseGcpCreateDisabledBanner />}
                    <KnowledgeBaseAskAi variant="list" />
                    <div className="knowledge-base-page__mobile-toolbar">
                        <PageFilterDropdown
                            className="knowledge-base-page__category-select"
                            inputId="knowledge-base-category-filter"
                            value={selectedCategoryUuid ?? KNOWLEDGE_BASE_ALL_CATEGORIES_VALUE}
                            options={categoryFilterOptions}
                            optionLabel="name"
                            optionValue="uuid"
                            filter={categories.length > 6}
                            showClear={false}
                            itemTemplate={renderCategoryFilterOption}
                            valueTemplate={renderCategoryFilterOption}
                            aria-label={t("knowledgeBase.categoriesLabel") as string}
                            onChange={(event) => {
                                const next = String(event.value ?? "");
                                selectCategory(next === KNOWLEDGE_BASE_ALL_CATEGORIES_VALUE || !next ? null : next);
                            }}
                        />
                        {canManage && (
                            <Button
                                type="button"
                                text
                                icon="pi pi-cog"
                                className="knowledge-base-page__mobile-manage"
                                aria-label={t("knowledgeBase.category.manage") as string}
                                onClick={() => setManageCategoriesVisible(true)}
                            />
                        )}
                    </div>
                    <PageFilters>
                        <PageFilterSearch
                            value={search}
                            onChange={setSearch}
                            placeholder={t("knowledgeBase.searchPlaceholder") as string}
                        />
                        {(hasSharedArticles || ownOnly) && (
                            <SmartFilterButton
                                className="knowledge-base-page__own-only"
                                label={t("knowledgeBase.filter.ownOnly", "Alleen eigen artikelen") as string}
                                active={ownOnly}
                                onClick={() => {
                                    setOwnOnly((current) => !current);
                                    setListPage(0);
                                    setSpotlightPage(0);
                                }}
                            />
                        )}
                    </PageFilters>

                    {spotlightArticles.length > 0 && (
                        <section className="knowledge-base-page__section">
                            <button
                                type="button"
                                className="knowledge-base-page__section-toggle"
                                aria-expanded={spotlightOpen}
                                onClick={() => setSpotlightOpen((open) => !open)}
                            >
                                <span className="knowledge-base-page__section-title">
                                    <i className="pi pi-bookmark-fill" />
                                    <i className="pi pi-star-fill" />
                                    {t("knowledgeBase.pinnedAndFeatured")}
                                    <span className="knowledge-base-page__count">{spotlightTotal}</span>
                                </span>
                                <i className={`pi ${spotlightOpen ? "pi-chevron-up" : "pi-chevron-down"}`} aria-hidden />
                            </button>
                            {spotlightOpen && (
                                <DataView
                                    value={spotlightArticles}
                                    dataKey="uuid"
                                    layout="grid"
                                    lazy
                                    listTemplate={articleListTemplate}
                                    paginator
                                    alwaysShowPaginator={false}
                                    rows={KNOWLEDGE_ARTICLE_SPOTLIGHT_PAGE_SIZE}
                                    first={spotlightPage * KNOWLEDGE_ARTICLE_SPOTLIGHT_PAGE_SIZE}
                                    totalRecords={spotlightTotal}
                                    onPage={(event) => setSpotlightPage(event.page ?? 0)}
                                />
                            )}
                        </section>
                    )}

                    <section className="knowledge-base-page__section">
                        <h3 className="knowledge-base-page__section-title">
                            <i className="pi pi-list" />
                            {t("knowledgeBase.allArticles")}
                        </h3>
                        <DataView
                            value={allArticles}
                            dataKey="uuid"
                            layout="list"
                            lazy
                            listTemplate={articleRowsTemplate}
                            loading={loading}
                            emptyMessage={t("knowledgeBase.noArticles") as string}
                            paginator
                            alwaysShowPaginator
                            rows={pageSize}
                            rowsPerPageOptions={[...KNOWLEDGE_ARTICLE_ROWS_PER_PAGE_OPTIONS]}
                            first={listPage * pageSize}
                            totalRecords={totalElements}
                            onPage={(event) => {
                                if (loading) {
                                    return;
                                }
                                const nextSize = event.rows ?? pageSize;
                                if (nextSize !== pageSize) {
                                    setPageSize(nextSize);
                                    setListPage(0);
                                    return;
                                }
                                setListPage(event.page ?? 0);
                            }}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate={t("datatable.filter.page-report", {
                                type: t("knowledgeBase.articles"),
                                first: "{first}",
                                last: "{last}",
                                totalRecords: "{totalRecords}",
                            }) as string}
                            className="knowledge-base-page__dataview"
                        />
                    </section>
                </div>
            </div>
        </div>
    );
};

export default KnowledgeBase;
